import React, {useEffect, useState} from "react";
import {getIdWithToken} from "../../../utils/storage/Token";
import FCSpinner from "../../FCObjets/FCSpinner";
import {useFetchBilling} from "../../../service/Business/BillingService";
import {CallbackErrorModalFL, CallbackFinishedModalFL} from "../../Dashboard/Form/ModalForm";
import FCAlertMsgErrorAPI from "../../FCObjets/FCAlertMsg/FCAlertMsgErrorAPI";
import {OutlayInterface, OutlayItemInterface} from "../../../service/Business/Interfaces/OutlayInterface";
import {AddOutlay, UpdateOutlay} from "../../../service/Business/OutlayService";
import {
    AddOutlayItem,
    DeleteOutlayItem,
    UpdateOutlayItem,
    UploadOutlayItem
} from "../../../service/Business/OutlayItemService";
import TableOutlayItem from "./Tables/TableOutlayItem";
import StatusOutlay from "../../../utils/Business/StatusOutlay";
import FCSelect, {FCSelectOption} from "../../FCObjets/Form/FCSelect";
import {CheckArchiveExists} from "../../../service/Business/ArchiveService";
import FCAlertMsg, {TypeAlertMsg} from "../../FCObjets/FCAlertMsg/FCAlertMsg";
import {useToast} from "../../FCObjets/FCToast/FCToast";
import "./FormAtelier.scss";

type FormOutlayProps = {
    onFinished: CallbackFinishedModalFL;
    onError: CallbackErrorModalFL;
    dataOutlay: OutlayInterface | undefined;
};

const FormOutlay: React.FunctionComponent<FormOutlayProps> = ({dataOutlay, onFinished, onError}) => {
    const toast = useToast();

    const [inputLabel, setInputLabel] = useState(dataOutlay ? dataOutlay.label : "");
    const [inputBilling, setInputBilling] = useState<number>(0);
    const [inputState, setInputState] = useState<number>(dataOutlay ? dataOutlay.state : 0);
    const [validate, setValidate] = useState(false);

    const [listOutlayItemAdd, setListOutlayItemAdd] = useState<OutlayItemInterface[]>([]);
    const [listOutlayItemDel, setListOutlayItemDel] = useState<OutlayItemInterface[]>([]);
    const [listOutlayItemModify, setListOutlayItemModify] = useState<OutlayItemInterface[]>([]);
    const [confirmOverwrite, setConfirmOverwrite] = useState<boolean>(false);
    const [overwriteLabels, setOverwriteLabels] = useState<string[]>([]);

    const {data: billings, loading, error} = useFetchBilling(getIdWithToken());
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (billings && dataOutlay?.billing) {
            const idx = billings.findIndex((b: any) => b.id === dataOutlay.billing.id);
            if (idx >= 0) setInputBilling(idx + 1);
        }
    }, [billings]);

    const handlerEventItems = (listAdd: OutlayItemInterface[], listDel: OutlayItemInterface[], listModify: OutlayItemInterface[]) => {
        setListOutlayItemAdd(listAdd);
        setListOutlayItemDel(listDel);
        setListOutlayItemModify(listModify);
    };

    const handleSubmit = async () => {
        const errors: Record<string, string> = {};

        if (!inputLabel.trim()) errors.inputLabel = "Le libellé est obligatoire.";

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            toast.error(Object.values(errors).join(" "), "Validation");
            return;
        }
        setFieldErrors({});

        // Si la confirmation n'a pas encore été donnée, on vérifie les écrasements potentiels
        // sur les justificatifs des outlay items modifiés.
        if (!confirmOverwrite) {
            const itemsAvecFichier = listOutlayItemModify.filter((item) => item.file);
            if (itemsAvecFichier.length > 0) {
                try {
                    const checks = await Promise.all(
                        itemsAvecFichier.map((item) =>
                            CheckArchiveExists("OUTLAY_ITEM", item.id).then((exists) => ({item, exists}))
                        )
                    );
                    const ecrasements = checks.filter((c) => c.exists);
                    if (ecrasements.length > 0) {
                        setOverwriteLabels(ecrasements.map((c) => c.item.label));
                        setConfirmOverwrite(true);
                        return;
                    }
                } catch {
                    // En cas d'erreur de vérification, on procède quand même au save (comportement actuel).
                }
            }
        }

        const billing = inputBilling > 0 && billings ? billings[inputBilling - 1] : dataOutlay?.billing;

        const outlay = {
            label: inputLabel,
            billing,
            outlayItems: [],
            state: inputState,
            id: dataOutlay ? dataOutlay.id : -1,
        };

        try {
            setValidate(true);

            if (listOutlayItemAdd.length !== 0) {
                const response = await AddOutlayItem(dataOutlay ? dataOutlay.id : 0, {listOutlayItem: listOutlayItemAdd});
                const data = response.data;
                const uploadPromises: Promise<any>[] = [];
                for (const item of listOutlayItemAdd) {
                    if (item.file) {
                        const match = data.find((d: any) => d.label === item.label);
                        if (match) uploadPromises.push(UploadOutlayItem(match.id, item.file));
                    }
                }
                await Promise.all(uploadPromises);
            }

            if (listOutlayItemDel.length !== 0) {
                await Promise.all(listOutlayItemDel.map((item) => DeleteOutlayItem(item)));
            }

            if (listOutlayItemModify.length !== 0) {
                await UpdateOutlayItem(getIdWithToken(), {listOutlayItem: listOutlayItemModify});
                const uploadPromises: Promise<any>[] = [];
                for (const item of listOutlayItemModify) {
                    if (item.file) uploadPromises.push(UploadOutlayItem(item.id, item.file));
                }
                await Promise.all(uploadPromises);
            }

            if (dataOutlay) {
                await UpdateOutlay(dataOutlay.id, outlay);
                toast.success("Frais de déplacement modifiés avec succès.", "Frais de déplacement");
            } else {
                await AddOutlay(outlay);
                toast.success("Frais de déplacement ajoutés avec succès.", "Frais de déplacement");
            }

            onFinished();
        } catch (err) {
            setValidate(false);
            onError(err);
        }
    };

    return (
        <form className="fcaForm" onSubmit={(e) => e.preventDefault()}>
            {loading && <FCSpinner size="small"/>}
            {error && <FCAlertMsgErrorAPI error={error}/>}

            {/* Informations générales */}
            <div className="fcaSection">
                <div className="fcaSectionLabel">Informations générales</div>
                <div className={`fcaField${fieldErrors.inputLabel ? " fcaField--error" : ""}`}>
                    <label htmlFor="label">Libellé</label>
                    <input id="label" type="text" value={inputLabel}
                           placeholder="ex. Déplacement client Toulouse"
                           onChange={(e) => setInputLabel(e.target.value)}/>
                    {fieldErrors.inputLabel && <span className="fcaFieldError">{fieldErrors.inputLabel}</span>}
                </div>
                <div className="fcaRow">
                    <div className="fcaField">
                        <label>Facture associée</label>
                        <FCSelect
                            options={[
                                {value: 0, label: "— Aucune —"},
                                ...(billings ? billings.map((b: any, i: number) => ({value: i + 1, label: b.number})) : [])
                            ] as FCSelectOption[]}
                            value={inputBilling}
                            onChange={(v) => setInputBilling(Number(v))}
                            placeholder="— Aucune —"
                        />
                    </div>
                    <div className="fcaField">
                        <label>Statut</label>
                        <FCSelect
                            options={StatusOutlay.ListStatus.map((s) => ({value: s, label: StatusOutlay.toString(s)}))}
                            value={inputState}
                            onChange={(v) => setInputState(Number(v))}
                        />
                    </div>
                </div>
            </div>

            {/* Lignes de frais */}
            {dataOutlay && (
                <div className="fcaSection">
                    <div className="fcaSectionLabel">Lignes de frais</div>
                    <TableOutlayItem
                        dataItemOutlay={dataOutlay.outlayItems}
                        dataOutlay={dataOutlay}
                        handlerEvent={handlerEventItems}
                    />
                </div>
            )}

            {confirmOverwrite && (
                <FCAlertMsg
                    activate={true}
                    type={TypeAlertMsg.WARNING}
                    title="Justificatif(s) déjà archivé(s)"
                    textMsg={`Un justificatif est déjà présent pour : ${overwriteLabels.join(", ")}. Confirmez pour l'écraser.`}
                />
            )}

            <div className="fcaActions">
                <button type="button" className="fcaBtn fcaBtnSecondary" onClick={() => onFinished()}>
                    Annuler
                </button>
                {validate
                    ? <div style={{display: "flex", alignItems: "center", padding: "0 16px"}}><FCSpinner size="small"/></div>
                    : <button type="button" className="fcaBtn fcaBtnPrimary" onClick={handleSubmit}>
                        <span className="material-symbols-outlined">check</span>
                        {confirmOverwrite ? "Confirmer l'écrasement" : (dataOutlay ? "Enregistrer" : "Créer la note de frais")}
                      </button>
                }
            </div>
        </form>
    );
};

export default FormOutlay;
